import { useState } from "react";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, ShieldAlert, Loader2 } from "lucide-react";
import axios from "axios";

/**
 * Browser-based admin panel for auditing and cleaning up "suspect" premium
 * users (grants that came from the old Stripe test-mode reclaim bug).
 *
 * Deliberately unlisted from navigation — you have to know the URL AND have
 * the CRON_SECRET (from the Emergent Secrets tab) to use it.
 *
 * Route: /admin/premium-audit
 */
const API = (path) => `${process.env.REACT_APP_BACKEND_URL}${path}`;

const VERDICT_META = {
  "legit-polar": {
    label: "Legit · Polar",
    className: "bg-press-green text-paper border-press-green",
  },
  "legit-stripe-live": {
    label: "Legit · Stripe Live",
    className: "bg-press-green text-paper border-press-green",
  },
  "suspect-reclaim": {
    label: "Suspect · test-mode reclaim",
    className: "bg-press-red text-paper border-press-red",
  },
  unknown: {
    label: "Unknown",
    className: "bg-paper-alt text-ink border-ink",
  },
};

export default function AdminPremiumAudit() {
  const [token, setToken] = useState("");
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState("");
  const [lastResetResult, setLastResetResult] = useState(null);

  const fetchUsers = async () => {
    if (!token.trim()) {
      setError("Enter your CRON_SECRET first.");
      return;
    }
    setLoading(true);
    setError("");
    setUsers(null);
    setLastResetResult(null);
    try {
      const r = await axios.get(API("/api/admin/premium-users"), {
        headers: { "X-Cron-Token": token.trim() },
      });
      setUsers(r.data.users || []);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        setError("Token rejected. Double-check your CRON_SECRET from the Emergent Secrets panel.");
      } else if (status === 503) {
        setError("Server says CRON_SECRET is not configured. Set it in your Emergent Secrets tab and redeploy.");
      } else {
        setError(e?.response?.data?.detail || e.message || "Request failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const suspectCount = (users || []).filter((u) => u._verdict === "suspect-reclaim").length;
  const legitCount = (users || []).filter((u) => u._verdict?.startsWith("legit")).length;

  const resetSuspects = async () => {
    if (!window.confirm(
      `Reset ${suspectCount} suspect user(s)? Only test-mode reclaims will be cancelled. Real Polar/Stripe-live payers are safe.`
    )) return;
    setResetting(true);
    setError("");
    try {
      const r = await axios.post(
        API("/api/admin/reset-test-reclaims"),
        {},
        { headers: { "X-Cron-Token": token.trim() } }
      );
      setLastResetResult(r.data);
      toast.success(`Reset ${r.data.modified} user${r.data.modified === 1 ? "" : "s"}.`);
      // Re-fetch to reflect the new state
      await fetchUsers();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Reset failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink px-4 py-10 sm:py-16" data-testid="admin-premium-audit">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <ShieldAlert className="text-press-red" size={28} />
          <h1 className="font-headline text-3xl sm:text-4xl font-black uppercase tracking-tight">
            Premium Audit
          </h1>
        </div>
        <p className="font-body italic text-ink-muted mb-8 max-w-2xl">
          Paste your <code className="bg-paper-alt border border-ink px-1">CRON_SECRET</code>{" "}
          from the Emergent Secrets panel. This tool lists every user marked
          Premium and flags any suspect "test-mode reclaims" so you can reset
          them in one click. Genuine Polar and Stripe-live payers are never
          touched.
        </p>

        {/* Token input */}
        <div className="border-2 border-ink bg-paper-surface p-4 sm:p-6 shadow-hard-sm">
          <label className="block font-mono uppercase tracking-widest text-[10px] text-ink-muted mb-2">
            CRON_SECRET
          </label>
          <div className="flex flex-wrap gap-3 items-center">
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && fetchUsers()}
              placeholder="paste your secret here"
              className="flex-1 min-w-[240px] bg-paper text-ink border-2 border-ink px-3 py-3 font-mono text-sm focus:outline-none"
              data-testid="admin-token-input"
              autoComplete="off"
            />
            <button
              type="button"
              onClick={fetchUsers}
              disabled={loading}
              className="inline-flex items-center gap-2 bg-ink text-paper font-mono uppercase tracking-widest text-xs px-5 py-3 border-2 border-ink shadow-hard-sm disabled:opacity-60"
              data-testid="admin-audit-fetch-btn"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : null}
              Run audit
            </button>
          </div>
          {error && (
            <div className="mt-3 inline-flex items-center gap-2 font-mono text-xs text-press-red" data-testid="admin-audit-error">
              <AlertCircle size={14} /> {error}
            </div>
          )}
        </div>

        {/* Results */}
        {users !== null && (
          <div className="mt-8">
            <div className="flex flex-wrap items-baseline gap-4 mb-4">
              <h2 className="font-headline text-2xl font-bold uppercase tracking-tight">
                Results
              </h2>
              <span className="font-mono text-xs uppercase tracking-widest text-ink-muted">
                {users.length} total · {legitCount} legit · {suspectCount} suspect
              </span>
            </div>

            {suspectCount > 0 && (
              <div className="mb-6 border-2 border-press-red bg-press-red/5 p-4 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-headline text-lg font-bold text-press-red">
                      {suspectCount} suspect user{suspectCount === 1 ? "" : "s"} to reset
                    </div>
                    <div className="font-body italic text-sm text-ink-secondary">
                      These were granted Premium by the old Stripe test-mode
                      reclaim bug. Resetting only cancels their fake Premium —
                      no real payer is affected.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={resetSuspects}
                    disabled={resetting}
                    className="inline-flex items-center gap-2 bg-press-red text-paper font-mono uppercase tracking-widest text-xs px-5 py-3 border-2 border-press-red shadow-hard-sm disabled:opacity-60"
                    data-testid="admin-reset-suspects-btn"
                  >
                    {resetting ? <Loader2 size={14} className="animate-spin" /> : null}
                    Reset {suspectCount} suspect{suspectCount === 1 ? "" : "s"}
                  </button>
                </div>
              </div>
            )}

            {lastResetResult && (
              <div className="mb-6 border-2 border-press-green bg-press-green/5 p-4 sm:p-5 inline-flex items-center gap-3">
                <CheckCircle2 className="text-press-green" size={20} />
                <div className="font-body">
                  <strong>{lastResetResult.modified}</strong> user
                  {lastResetResult.modified === 1 ? "" : "s"} reset ·{" "}
                  {lastResetResult.matched} matched. {lastResetResult.note}
                </div>
              </div>
            )}

            {users.length === 0 ? (
              <div className="font-body italic text-ink-muted">
                No Premium users found on this environment. Nothing to audit.
              </div>
            ) : (
              <div className="border-2 border-ink bg-paper-surface overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-users-table">
                  <thead>
                    <tr className="border-b-2 border-ink font-mono uppercase tracking-widest text-[10px] text-ink-muted">
                      <th className="text-left p-3">Email</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Provider</th>
                      <th className="text-left p-3">Verdict</th>
                      <th className="text-left p-3">Premium since</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono text-xs">
                    {users.map((u) => {
                      const v = VERDICT_META[u._verdict] || VERDICT_META.unknown;
                      return (
                        <tr key={u.user_id} className="border-b border-dotted border-ink/40">
                          <td className="p-3 break-all">{u.email || <em className="text-ink-muted">(no email)</em>}</td>
                          <td className="p-3">{u.subscription_status || "—"}</td>
                          <td className="p-3">{u.premium_provider || "—"}</td>
                          <td className="p-3">
                            <span className={`inline-block px-2 py-0.5 border-2 text-[10px] uppercase tracking-widest ${v.className}`}>
                              {v.label}
                            </span>
                          </td>
                          <td className="p-3 text-ink-muted">
                            {u.premium_since ? u.premium_since.slice(0, 10) : "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Footer hint */}
        <div className="mt-16 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
          Keep this URL private · CRON_SECRET is validated server-side only
        </div>
      </div>
    </div>
  );
}
